import { Link, useParams, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, PageHero } from "@/components/Reveal";
import { PROGRAMS } from "@/lib/content";
import { PROGRAMS_HI } from "@/lib/content_hi";
import { useLang } from "@/context/LanguageContext";
import { BrochureButtons } from "@/components/Brochure";

const Level = ({ lvl, i }) => (
  <Reveal delay={0.1 * i}>
    <div className="relative p-7 rounded-2xl border border-slate-200 bg-white shadow-[0_8px_30px_rgba(10,25,47,0.06)] hover:border-[#00E5FF]/50 hover:shadow-[0_0_30px_rgba(0,229,255,0.12)] transition-[border-color,box-shadow] duration-500 h-full" data-testid={`level-${i}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#007A8A] font-bold"><GraduationCap className="w-4 h-4" />{lvl.grade}</div>
      <h3 className="text-2xl font-bold mt-3">{lvl.name}</h3>
      <ul className="mt-5 space-y-3">
        {lvl.topics.map((t, j) => (
          <li key={t} className="flex gap-3 text-slate-700 text-sm">
            <span className="font-mono text-[#FF2E63] text-xs mt-0.5">{String(j + 1).padStart(2, "0")}</span>{t}
          </li>
        ))}
      </ul>
    </div>
  </Reveal>
);

export default function Curriculum() {
  const { slug } = useParams();
  const { L, lang } = useLang();
  const base = PROGRAMS[slug];
  if (!base) return <Navigate to="/" replace />;
  const p = lang === "hi" ? { ...base, ...PROGRAMS_HI[slug] } : base;

  return (
    <div data-testid={`curriculum-${slug}`}>
      <PageHero chip={L(`${p.key} Curriculum · Classes 6–12`, `${p.key} पाठ्यक्रम · कक्षा 6–12`)} title={p.title} subtitle={p.tagline} image={p.image} testid="curriculum-hero">
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={`/enroll?program=${p.key}`}><Button size="lg" className="rs-cta rounded-full px-7" data-testid="curriculum-enroll-btn">{L(`Enroll in ${p.key}`, `${p.key} में दाखिला लें`)} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          <Link to="/schools"><Button size="lg" variant="outline" className="rounded-full bg-transparent border-slate-400 text-[#0A192F] hover:bg-slate-100 hover:text-[#0A192F]" data-testid="curriculum-school-btn">{L("Bring to my school", "मेरे स्कूल में लाएँ")}</Button></Link>
        </div>
        <div className="mt-4"><BrochureButtons slug={slug} /></div>
      </PageHero>

      <section className="bg-[#F0F4F8] text-[#0A192F] py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-10">{L("Grade-wise learning path", "कक्षावार सीखने का मार्ग")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {p.levels.map((l, i) => <Level key={l.grade} lvl={l} i={i} />)}
          </div>
        </div>
      </section>

      <section className="bg-[#F0F4F8] py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <h2 className="text-3xl font-extrabold text-[#0A192F]">{L("What students will achieve", "छात्र क्या हासिल करेंगे")}</h2>
            <ul className="mt-6 grid sm:grid-cols-2 gap-4">
              {p.outcomes.map((o) => (
                <li key={o} className="flex gap-3 p-4 rounded-xl bg-[#E8F0FE] border border-slate-200 text-[#0A192F]"><CheckCircle2 className="w-5 h-5 text-[#FF2E63] shrink-0" />{o}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <h2 className="text-3xl font-extrabold text-[#0A192F]">{L("Tools & platforms", "टूल्स और प्लेटफ़ॉर्म")}</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {p.tools.map((t) => <span key={t} className="px-4 py-2 rounded-full border border-slate-300 text-sm font-semibold text-[#0A192F]">{t}</span>)}
            </div>
            <div className="mt-8 p-6 rounded-2xl bg-[#F0F4F8] text-[#0A192F]">
              <div className="text-xs uppercase tracking-widest text-[#007A8A] font-bold">{L("Learn & Earn", "सीखें और कमाएँ")}</div>
              <p className="mt-2 text-slate-700 text-sm">{L("Top projects from this program get refined into RoboSpark products — students earn a share of sales.", "इस कार्यक्रम के सर्वश्रेष्ठ प्रोजेक्ट RoboSpark उत्पाद बनते हैं — छात्रों को बिक्री में हिस्सा मिलता है।")}</p>
              <Link to="/learn-and-earn" className="mt-4 inline-flex items-center gap-1 text-[#FF2E63] font-semibold text-sm" data-testid="curriculum-learn-earn-link">{L("See the model", "मॉडल देखें")} <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
